import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { uploadAsset } from '@/api/assets';
import { analyzeEmbroiderySource } from '@/api/embroidery';
import { toast } from 'react-hot-toast';
import { logger } from '@/utils/logger';
import { loadImage } from '@/utils/mockupLayers';
import { proxyStorageUrl } from '@/lib/product-transformer';
import {
  embroideryMaxRelWidth,
  useGuidedEntryStore,
  type GuidedEmbroidery,
  type GuidedSide,
} from '@/stores/guidedEntryStore';
import { useEmbroideryStore } from '@/stores/embroideryStore';
import {
  MAX_UPLOAD_BYTES,
  THREAD_COLORS,
  threadById,
  type PrintContext,
} from '@designdeck/shared';
import { EmbroideryThreadModal } from '@/components/editor/EmbroideryThreadModal';
import {
  PREVIEW_EDGE,
  canvasToBlob,
  printResolutionEdge,
  renderEmbroideryPreview,
} from '@/lib/embroidery/renderPreview';
import type { GuidedEmbroideryLimits } from './GuidedMotifCard';

/** Assets API refuses anything larger, and retrying a refusal is pointless. */
const MAX_RENDER_BYTES = MAX_UPLOAD_BYTES;

/**
 * Threads block of an embroidery card: open the modal, show the confirmed
 * threads, and turn a confirmed mapping into the artwork.
 *
 * Confirming does what the editor's Apply does (see useEmbroideryMapping):
 * render the stitch optics at print resolution from the original pixels,
 * store the render as the asset, remember the mapping. Here the render
 * becomes the side's motif — the guided sync then writes it into canvas
 * state with `embFileKey` and `confirmedMapping` on the object, the same
 * marks the publish rules look for.
 */
export function EmbroideryControls({
  side,
  embroidery,
  limits,
  printContext,
  onError,
}: {
  side: GuidedSide;
  embroidery: GuidedEmbroidery;
  limits: GuidedEmbroideryLimits;
  printContext: PrintContext;
  onError: (message: string | null) => void;
}) {
  const { t } = useTranslation();
  const confirmEmbroidery = useGuidedEntryStore((s) => s.confirmEmbroidery);
  const setEmbroideryPalette = useGuidedEntryStore(
    (s) => s.setEmbroideryPalette
  );
  const clearMotif = useGuidedEntryStore((s) => s.clearMotif);
  const isModalOpen = useEmbroideryStore((s) => s.isModalOpen);
  const modalKey = useEmbroideryStore((s) => s.source.embFileKey);
  const [isOpening, setIsOpening] = useState(false);

  // The ceiling and the DPI come from the reference position; the store
  // holds them for the modal's counter and for the render.
  useEffect(() => {
    const store = useEmbroideryStore.getState();
    store.setMaxColors(limits.maxColors);
    store.setMinDpi(limits.minDpi);
  }, [limits.maxColors, limits.minDpi]);

  const storageUrl = proxyStorageUrl(embroidery.embFileKey);
  const sourceUrl = embroidery.sourceUrl ?? storageUrl;

  /**
   * The original pixels. The blob URL of the upload is preferred, but the
   * thread store is shared with the editor and revokes it on a product
   * detour — the source in storage is the same image, so it stands in and
   * the dead URL is dropped for good.
   */
  const loadSource = async (): Promise<HTMLImageElement> => {
    if (embroidery.sourceUrl) {
      try {
        return await loadImage(embroidery.sourceUrl);
      } catch {
        setEmbroideryPalette(side, embroidery.palette, null);
      }
    }
    if (!storageUrl) {
      throw new Error(t('creator.guidedMotif.sourceUnreachable'));
    }
    return loadImage(storageUrl);
  };

  const openThreads = async () => {
    if (isOpening) return;
    setIsOpening(true);
    onError(null);
    try {
      let palette = embroidery.palette;
      if (palette.length === 0) {
        // After a reload the palette is gone with the session; the source
        // is still in storage and the extraction is deterministic, so the
        // keys line up with the stored mapping.
        palette = await analyzeEmbroiderySource(embroidery.embFileKey);
        setEmbroideryPalette(side, palette);
      }
      useEmbroideryStore.getState().startMapping({
        palette,
        existingMapping: embroidery.mapping ?? {},
        objectUrl: embroidery.sourceUrl,
        embFileKey: embroidery.embFileKey,
        openModal: true,
      });
    } catch (err) {
      logger.warn('GuidedMotifCard', 'Could not reopen the threads', err);
      onError(t('creator.guidedMotif.threadsLoadFailed'));
    } finally {
      setIsOpening(false);
    }
  };

  /**
   * The modal resets its busy flag on failure but does not catch — the
   * error is ours to show. The modal stays open and the mapping is kept,
   * so the seller can retry instead of mapping again.
   */
  const confirm = async (mapping: Record<string, string>) => {
    try {
      await renderAndStore(mapping);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      logger.error('GuidedMotifCard', 'Embroidery apply failed:', msg);
      toast.error(t('creator.toast.threadsApplyFailed', { message: msg }));
    }
  };

  const renderAndStore = async (mapping: Record<string, string>) => {
    if (!sourceUrl) return;
    const palette = useEmbroideryStore.getState().palette;
    const image = await loadSource();

    // The render is sized from the motif's placed size, not the field — a
    // small logo in a large field would otherwise be rendered by the ratio
    // between the two.
    // Sized for the largest width the field allows, not the width placed
    // right now: the seller sizes the motif after confirming, and a render
    // made for 60 mm would ship under-resolved at 30 cm. The source edge
    // still caps it — nothing is upscaled.
    const relWidth = embroideryMaxRelWidth(embroidery, printContext);
    const widthMm =
      printContext.widthMm > 0
        ? relWidth * printContext.widthMm
        : (relWidth * printContext.widthPx) / printContext.pxPerMm;
    const heightMm = widthMm * (embroidery.height / embroidery.width);
    const maxEdge = printResolutionEdge({
      widthMm,
      heightMm,
      minDpi: limits.minDpi,
      sourceEdge: Math.max(image.naturalWidth, image.naturalHeight),
    });

    const render = (edge: number) =>
      renderEmbroideryPreview({
        image,
        palette,
        mapping,
        threads: THREAD_COLORS,
        maxEdge: edge,
      });

    let rendered = render(maxEdge);
    if (rendered.degraded) {
      // A tainted source or no thread layer at all: uploading that would
      // confirm a motif nobody mapped as the production artwork.
      throw new Error(t('creator.guidedMotif.stitchRenderFailed'));
    }
    let blob = await canvasToBlob(rendered.canvas);
    if (blob.size > MAX_RENDER_BYTES) {
      // Dense hatching encodes badly; the interactive edge keeps the upload
      // inside the asset limit.
      rendered = render(PREVIEW_EDGE);
      blob = await canvasToBlob(rendered.canvas);
      if (blob.size > MAX_RENDER_BYTES) {
        throw new Error(t('creator.guidedMotif.renderTooLarge'));
      }
    }

    const asset = await uploadAsset(
      new File([blob], 'embroidery-render.png', { type: 'image/png' })
    );
    confirmEmbroidery(side, {
      motif: {
        assetId: asset.id,
        url: asset.url,
        width: rendered.canvas.width,
        height: rendered.canvas.height,
        fileName: embroidery.fileName,
      },
      mapping,
    });
    useEmbroideryStore.getState().commitDraft();
  };

  const threadIds = embroidery.mapping
    ? Array.from(new Set(Object.values(embroidery.mapping)))
    : [];

  return (
    <div className="flex flex-col gap-2.5">
      {embroidery.mapping ? (
        <div className="flex items-center gap-2.5 rounded-control bg-[var(--paper-2)] px-3 py-2.5">
          <span className="flex items-center gap-1" aria-hidden="true">
            {threadIds.map((id) => (
              <span
                key={id}
                className="h-3.5 w-3.5 rounded-full ring-1 ring-black/15"
                style={{ background: threadById(id)?.hex ?? '#888' }}
              />
            ))}
          </span>
          <span className="dc-type-data text-[var(--ink)]">
            {t('creator.guidedMotif.threadSummary', {
              count: threadIds.length,
              max: limits.maxColors,
            })}
          </span>
          <button
            type="button"
            onClick={() => void openThreads()}
            disabled={isOpening}
            className="ml-auto h-8 cursor-pointer rounded-control px-3 font-display text-[12.5px] font-semibold text-[var(--ink)] ring-1 ring-inset ring-[var(--line)] hover:bg-[var(--paper)] disabled:opacity-60"
          >
            {t('creator.guidedMotif.editThreads')}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2 rounded-control bg-[var(--paper-2)] px-3 py-2.5">
          <span className="dc-type-label text-[var(--cobalt-500)]">
            {t('creator.guidedMotif.threadsOpen')}
          </span>
          <span className="text-[12.5px] leading-[1.45] text-[var(--mute)]">
            {t('creator.guidedMotif.threadsOpenHint')}
          </span>
          <div>
            <button
              type="button"
              onClick={() => void openThreads()}
              disabled={isOpening}
              className="h-9 cursor-pointer rounded-control bg-[var(--cobalt-500)] px-3.5 font-display text-[13px] font-semibold text-white hover:brightness-95 disabled:opacity-60"
            >
              {t('creator.guidedMotif.setThreads')}
            </button>
          </div>
        </div>
      )}

      <EmbroideryThreadModal
        isOpen={isModalOpen && modalKey === embroidery.embFileKey}
        sourceUrl={sourceUrl}
        onCancel={() => useEmbroideryStore.getState().closeModal()}
        onConfirm={confirm}
        onRemoveMotif={() => {
          clearMotif(side);
          useEmbroideryStore.getState().clearEmbroidery();
        }}
      />
    </div>
  );
}
